import React, { useState } from 'react';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  ListItemButton,
  Divider,
  Toolbar,
  Box,
  Typography,
  Avatar,
  Chip
} from '@mui/material';
import { 
  Home, 
  Category, 
  Description, 
  AccountBalance,
  Info,
  Language,
  HelpOutline,
  Feedback as FeedbackIcon,
  Shield,
  Article,
  FolderOpen
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { FeedbackModal } from './FeedbackModal';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  variant: 'permanent' | 'temporary';
}

export const Sidebar: React.FC<SidebarProps> = ({ open, onClose, variant }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { currentLanguage, setLanguage } = useLanguage();
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  const menuItems = [
    { 
      text: t('home'), 
      icon: <Home />, 
      path: '/',
      subtitle: t('go_to_homepage'),
      bgColor: 'rgba(103,58,183,0.08)'
    },
    { 
      text: t('government_schemes'), 
      icon: <AccountBalance />, 
      path: '/schemes',
      subtitle: t('explore_welfare_schemes'),
      bgColor: '#E3F2FD'
    }
  ];

  const settingsItems = [
    { 
      text: t('language_settings'), 
      icon: <Language />, 
      action: 'language',
      subtitle: t('change_app_language'),
      bgColor: 'rgba(103,58,183,0.08)'
    },
    { 
      text: t('feedback'), 
      icon: <FeedbackIcon />, 
      action: 'feedback',
      subtitle: t('share_feedback'),
      bgColor: '#FFF3E0'
    },
    { 
      text: t('help_faq'), 
      icon: <HelpOutline />, 
      path: '/contact',
      subtitle: t('get_help_support'),
      bgColor: 'rgba(103,58,183,0.08)'
    },
    { 
      text: t('privacy_policy'), 
      icon: <Shield />, 
      path: '/privacy_policy',
      subtitle: t('view_privacy_policy'),
      bgColor: 'rgba(103,58,183,0.08)'
    },
    { 
      text: t('terms_of_service'), 
      icon: <Article />, 
      path: '/terms_of_service',
      subtitle: t('view_terms_conditions'),
      bgColor: '#E3F2FD'
    },
    { 
      text: t('about'), 
      icon: <Info />, 
      path: '/about',
      subtitle: t('app_information'),
      bgColor: '#E3F2FD'
    },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (variant === 'temporary') {
      onClose();
    }
  };

  const handleAction = (action: string) => {
    if (action === 'language') {
      setLanguage(currentLanguage === 'en' ? 'hi' : 'en');
    } else if (action === 'feedback') {
      setFeedbackModalOpen(true);
      if (variant === 'temporary') {
        onClose();
      }
    }
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Spacer for navbar */}
      <Toolbar />
      
      {/* Menu Items */}
      <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
        <List sx={{ pt: 1 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ px: 1.5, py: 0.5 }}>
              <ListItemButton 
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: 2,
                  py: 1.25,
                  px: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(103,58,183,0.08)',
                    '&:hover': {
                      bgcolor: 'rgba(103,58,183,0.12)',
                    },
                  },
                  '&:hover': {
                    bgcolor: 'rgba(103,58,183,0.04)',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 48 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: item.bgColor, 
                      width: 36, 
                      height: 36,
                      color: '#673AB7'
                    }}
                  >
                    {item.icon}
                  </Avatar>
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  secondary={item.subtitle}
                  primaryTypographyProps={{
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    fontSize: '0.9rem'
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.7rem',
                    color: 'text.secondary'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 1 }} />

        <List>
          {settingsItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ px: 1.5, py: 0.5 }}>
              <ListItemButton 
                selected={!!(item.path && location.pathname === item.path)}
                onClick={() => item.action ? handleAction(item.action) : item.path && handleNavigation(item.path)}
                sx={{
                  borderRadius: 2,
                  py: 1.25,
                  px: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(103,58,183,0.08)',
                    '&:hover': {
                      bgcolor: 'rgba(103,58,183,0.12)',
                    },
                  },
                  '&:hover': {
                    bgcolor: 'rgba(103,58,183,0.04)',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 48 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: item.bgColor, 
                      width: 36, 
                      height: 36,
                      color: '#673AB7'
                    }}
                  >
                    {item.icon}
                  </Avatar>
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  secondary={item.subtitle}
                  primaryTypographyProps={{
                    fontWeight: item.path && location.pathname === item.path ? 600 : 400,
                    fontSize: '0.9rem'
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.7rem',
                    color: 'text.secondary'
                  }}
                />
                {item.action === 'language' && (
                  <Chip 
                    label={currentLanguage === 'en' ? 'EN' : 'HI'} 
                    size="small"
                    sx={{ 
                      bgcolor: 'white',
                      border: '1px solid #E0E0E0',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      height: 24
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile drawer */}
      {variant === 'temporary' && (
        <Drawer
          variant="temporary"
          open={open}
          onClose={onClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
      
      {/* Desktop drawer */}
      {variant === 'permanent' && (
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: 280,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Feedback Modal */}
      <FeedbackModal 
        open={feedbackModalOpen} 
        onClose={() => setFeedbackModalOpen(false)} 
      />
    </>
  );
};
